// Typed API client on openapi-fetch (FR-030, FR-033 to FR-035; contracts/session-seam.md §4).
//
// - Paths and response types come only from the generated contract (no hand-written ones).
// - `Authorization: Bearer` is attached only while a credential exists.
// - Cookies: `include` on the web, `omit` on Android (apiCredentials platform pair).
// - A default 15 s timeout; the timeout surfaces as the `timeout` error kind.
// - A `401` on a request that carried a credential calls `session.onUnauthenticated()` once.
// - The layer adds no school identifier and logs nothing about requests (FR-034, SA-AUTH-2).
import createClient from "openapi-fetch";

import { API_CREDENTIALS } from "./apiCredentials";
import { type AppError, toAppError } from "./errors";
import { trackRequest } from "./slowRequests";
import type { Paths } from "./types";

export const DEFAULT_TIMEOUT_MS = 15_000;

export type ApiSession = {
  getAccessToken(): string | null;
  onUnauthenticated(): void;
};

export type ApiClientOptions = {
  /** PublicConfig.apiBaseUrl: scheme, host and optional port; contract paths are written in full. */
  baseUrl: string;
  /** Tests inject a contract-validating mock. */
  fetch?: (input: Request) => Promise<Response>;
  timeoutMs?: number;
  session: ApiSession;
};

export type ApiClient = ReturnType<typeof createClient<Paths>>;

class TimeoutError extends Error {
  constructor() {
    super("The request timed out.");
    this.name = "TimeoutError";
  }
}

export function createApiClient({
  baseUrl,
  fetch: baseFetch = (request) => fetch(request),
  timeoutMs = DEFAULT_TIMEOUT_MS,
  session,
}: ApiClientOptions): ApiClient {
  async function authFetch(request: Request): Promise<Response> {
    const token = session.getAccessToken();
    const prepared = new Request(request, { credentials: API_CREDENTIALS });
    if (token) prepared.headers.set("Authorization", `Bearer ${token}`);

    const controller = new AbortController();
    const forward = () => controller.abort();
    request.signal.addEventListener("abort", forward);
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);
    try {
      const response = await trackRequest(
        baseFetch(new Request(prepared, { signal: controller.signal })),
      );
      if (response.status === 401 && token) session.onUnauthenticated();
      return response;
    } catch (thrown) {
      throw timedOut ? new TimeoutError() : thrown;
    } finally {
      clearTimeout(timer);
      request.signal.removeEventListener("abort", forward);
    }
  }

  return createClient<Paths>({
    baseUrl: baseUrl.replace(/\/+$/, ""),
    fetch: authFetch,
  });
}

type FetchResult<T> = { data?: T; error?: unknown; response: Response };

/**
 * Awaits an openapi-fetch call and returns its data, or throws one AppError: no network, timeout,
 * a problem body, or any other failing status.
 */
export async function unwrap<T>(
  request: Promise<FetchResult<T>> | FetchResult<T>,
): Promise<T> {
  let result: FetchResult<T>;
  try {
    result = await request;
  } catch (thrown) {
    throw toAppError({ thrown, timedOut: false });
  }
  if (!result.response.ok) {
    throw toAppError({ response: result.response, body: result.error });
  }
  return result.data as T;
}

export type { AppError };
